import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Stack,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { BugReport as BugReportIcon } from '@mui/icons-material';
import { classificationApi } from '../../services/api';

const DefectTypeDialog = ({ 
  open, 
  onClose, 
  mode = 'create', // 'create' or 'edit'
  assetTypeId,
  defectType = null,
  userData,
  onUpdate 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    asset_type_id: assetTypeId || '',
    organization_id: userData?.organizationId || '',
    is_trafficsafety_related: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && defectType) {
        setFormData({
          name: defectType.name || '',
          code: defectType.code || '',
          description: defectType.description || '',
          asset_type_id: defectType.asset_type_id || assetTypeId || '',
          organization_id: defectType.organization_id || userData?.organizationId || '',
          is_trafficsafety_related: defectType.is_trafficsafety_related || false
        });
      } else {
        setFormData({
          name: '',
          code: '',
          description: '',
          asset_type_id: assetTypeId || '',
          organization_id: userData?.organizationId || '',
          is_trafficsafety_related: false
        });
      }
      setError(null);
    }
  }, [open, mode, defectType, assetTypeId, userData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Defect type name is required');
      return;
    }
    
    if (!formData.code.trim()) {
      setError('Defect type code is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (mode === 'edit' && defectType) {
        await classificationApi.updateDefectType(defectType.id, formData);
      } else {
        await classificationApi.createDefectType(formData);
      }

      // Notify parent component to refresh data
      if (onUpdate) {
        await onUpdate();
      }

      // Close dialog
      onClose();
    } catch (err) {
      console.error('Error saving defect type:', err);
      setError(err.response?.data?.error || 'Failed to save defect type');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!defectType || mode !== 'edit') return;

    if (!window.confirm(`Are you sure you want to delete the defect type "${defectType.name}"?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await classificationApi.deleteDefectType(defectType.id);

      // Notify parent component to refresh data
      if (onUpdate) {
        await onUpdate();
      }

      // Close dialog
      onClose();
    } catch (err) {
      console.error('Error deleting defect type:', err);
      setError(err.response?.data?.error || 'Failed to delete defect type');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'error.main', color: 'error.contrastText', display: 'flex', alignItems: 'center', gap: 1 }}>
        <BugReportIcon />
        {mode === 'create' ? 'Create' : 'Edit'} Defect Type
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Basic Information
            </Typography>
            
            <TextField
              label="Defect Type Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              fullWidth
              placeholder="e.g., Pothole, Crack, Surface Damage"
              helperText="Enter a descriptive name for this defect type"
              disabled={loading}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Defect Code"
              value={formData.code}
              onChange={(e) => handleInputChange('code', e.target.value)}
              required
              fullWidth
              placeholder="e.g., PH01, CR02, SD03"
              helperText="Enter a unique code for this defect type"
              disabled={loading}
            />
          </Box>

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Describe the characteristics and identification criteria for this defect..."
            helperText="Optional: Provide detailed description of this defect type"
            disabled={loading}
          />

          {/* Traffic Safety Checkbox - only for road assets (asset_type_id = 1) */}
          {formData.asset_type_id === 1 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Safety Configuration
              </Typography>
              
              <Box sx={{ p: 2, backgroundColor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_trafficsafety_related}
                      onChange={(e) => handleInputChange('is_trafficsafety_related', e.target.checked)}
                      color="warning"
                      disabled={loading}
                    />
                  }
                  label="Traffic Safety Related"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Check this if this defect type poses immediate traffic safety concerns
                </Typography>
              </Box>
            </Box>
          )}

          {/* Preview information */}
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Preview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Name:</strong> {formData.name || 'Not specified'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Code:</strong> {formData.code || 'Not specified'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Organization:</strong> {userData?.organizationName}
            </Typography>
            {formData.asset_type_id === 1 && (
              <Typography variant="body2" color="text.secondary">
                <strong>Traffic Safety:</strong> {formData.is_trafficsafety_related ? 'Yes' : 'No'}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        {mode === 'edit' && (
          <Button
            onClick={handleDelete}
            color="error"
            disabled={loading}
            sx={{ mr: 'auto' }}
          >
            Delete
          </Button>
        )}
        
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          color="error"
          sx={{ px: 3 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            mode === 'create' ? 'Create Defect Type' : 'Update Defect Type'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DefectTypeDialog; 